import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../auth/authSlice';
import {
    useGenerateQuizSetQuery,
    useCreateQuizAttemptMutation
} from '../product_knowledge/productKnowledgeApiSlice';
import {
    Container,
    Header,
    Button,
    Card,
    Icon,
    Loader,
    Progress,
    Message,
    Form,
    Radio,
    TextArea,
    Segment
} from 'semantic-ui-react';
import { useBreakpoint } from '../../hooks/useBreakpoint';

const TIME_LIMIT_PER_QUESTION = 60; // 60 seconds per question

const QuizSession = () => {
    const { isMobile } = useBreakpoint();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const mode = queryParams.get('mode') || 'practice'; // 'practice' or 'test'
    const productId = queryParams.get('product_id');

    const user = useSelector(selectCurrentUser);
    const isAdmin = user?.is_staff || user?.is_superuser;

    const { 
        data: questions, 
        isLoading, 
        error 
    } = useGenerateQuizSetQuery({ 
        count: 10, 
        product_id: productId 
    });

    const [createAttempt] = useCreateQuizAttemptMutation();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({}); // { questionId: answerText }
    const [results, setResults] = useState({}); // { questionId: { is_correct, feedback } }
    const [isSubmitting, setIsSubmitting] = useState(false);
    // isSubmitting disables the button only after a re-render, so two taps in the same frame
    // both submitted. The ref blocks the second one synchronously (#249).
    const submittingRef = useRef(false);
    // The timer auto-submits once. It used to re-fire whenever a submit failed (timeLeft
    // stays 0), looping and re-saving every answer each time the 30 s duplicate window
    // lapsed — ~50 attempts for one trainee on 2026-10-20 (support ticket 12a6b197).
    const autoSubmittedRef = useRef(false);
    const [sessionComplete, setSessionComplete] = useState(false);

    // For Test Mode: Track detailed results after bulk submission
    const [testResults, setTestResults] = useState(null); 
    const [timeLeft, setTimeLeft] = useState(null);


    const currentQuestion = questions ? questions[currentIndex] : null;
    const progress = questions ? ((currentIndex + 1) / questions.length) * 100 : 0;

    const handleOptionSelect = (option) => {
        if (results[currentQuestion.id]) return; // Prevent changing after submission in Practice mode
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: option }));
    };

    const handleTextChange = (e, { value }) => {
        if (results[currentQuestion.id]) return;
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    };

    // Practice Mode: Check Answer Immediately
    const handleCheckAnswer = async () => {
        if (!currentQuestion || submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            const payload = {
                question: currentQuestion.id,
                user_answer: answers[currentQuestion.id] || '',
                is_correct: false // Let backend decide
            };
            const result = await createAttempt(payload).unwrap();
            
            setResults(prev => ({
                ...prev,
                [currentQuestion.id]: {
                    is_correct: result.is_correct,
                    feedback: result.grading_feedback
                }
            }));
        } catch (err) {
            console.error('Failed to submit answer:', err);
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setSessionComplete(true);
        }
    };

    // Test Mode: Submit All
    const handleSubmitTest = async (e) => {
        if (!questions || submittingRef.current) return;

        // If manual submission (triggered by click), check for empty answers — unless time is
        // up, when blanks are simply wrong (the retry after a failed auto-submit).
        if (e && e.target && timeLeft !== 0) {
            const hasUnanswered = questions.some(q => {
                const val = answers[q.id];
                return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
            });

            if (hasUnanswered) {
                alert('모든 문제에 답을 입력해주세요.');
                return;
            }
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        
        const finalResults = [];
        let correctCount = 0;

        const isBlank = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
        try {
            // Blank answers (time ran out) are wrong, not submitted: the server rejects an
            // empty answer, and one rejection used to fail the whole test.
            const responses = await Promise.all(questions.map(q =>
                isBlank(answers[q.id])
                    ? Promise.resolve({ question: q, result: { is_correct: false, grading_feedback: '시간 내에 답하지 않았습니다.' } })
                    : createAttempt({ question: q.id, user_answer: answers[q.id], is_correct: false })
                        .unwrap().then(res => ({ question: q, result: res }))
            ));

            responses.forEach(({ question, result }) => {
                finalResults.push({
                    id: question.id,
                    text: question.question_text,
                    user_answer: answers[question.id] || '',
                    is_correct: result.is_correct,
                    feedback: result.grading_feedback
                });
                if (result.is_correct) correctCount++;
            });

            setTestResults({
                total: questions.length,
                correct: correctCount,
                details: finalResults
            });
            setSessionComplete(true);

        } catch (err) {
            console.error('Test submission failed:', err);
            alert('제출 중 오류가 발생했습니다. 잠시 후 제출 버튼을 다시 눌러 주세요.');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    // Timer Logic for Test Mode
    useEffect(() => {
        if (mode === 'test' && questions?.length > 0 && timeLeft === null) {
            setTimeLeft(questions.length * TIME_LIMIT_PER_QUESTION);
        }
    }, [mode, questions, timeLeft]);

    useEffect(() => {
        if (timeLeft === null || timeLeft <= 0 || sessionComplete) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, sessionComplete]);

    useEffect(() => {
        if (timeLeft === 0 && mode === 'test' && !sessionComplete && !isSubmitting && !autoSubmittedRef.current) {
             autoSubmittedRef.current = true;
             handleSubmitTest(); // Auto-submit, once
        }
    }, [timeLeft, mode, sessionComplete, isSubmitting]);

    if (isLoading) return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FBFBFD' }}>
            <Loader active inline='centered' size='large'>퀴즈 생성 중...</Loader>
        </div>
    );
    if (error) return (
        <Container style={{ padding: '40px', textAlign: 'center' }}>
            <Message error header="오류 발생" content="퀴즈를 불러오는데 실패했습니다." style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
            <Button onClick={() => navigate('/quiz/dashboard')} style={{ marginTop: '20px', borderRadius: '20px' }}>대시보드로 돌아가기</Button>
        </Container>
    );
    if (!questions || questions.length === 0) return (
        <Container style={{ padding: '40px', textAlign: 'center' }}>
            <Message info header="문제 없음" content="조건에 맞는 퀴즈 문제가 없습니다." style={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
            <Button onClick={() => navigate('/quiz/dashboard')} style={{ marginTop: '20px', borderRadius: '20px' }}>대시보드로 돌아가기</Button>
        </Container>
    );

    // --- Result View ---
    if (sessionComplete) {
        if (mode === 'practice') {
            return (
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FBFBFD' }}>
                    <Container style={{ padding: '40px', maxWidth: '600px', textAlign: 'center' }}>
                        <div style={{ 
                            backgroundColor: 'white', 
                            padding: '40px', 
                            borderRadius: '24px', 
                            boxShadow: '0 10px 30px rgba(0,0,0,0.05)' 
                        }}>
                            <Icon name="flag checkered" size="huge" style={{ color: '#FFD60A', marginBottom: '20px' }} />
                            <Header as="h2" style={{ color: '#1d1d1f', marginBottom: '10px' }}>학습 완료!</Header>
                            <p style={{ color: '#86868b', fontSize: '16px', marginBottom: '30px' }}>연습 세션을 모두 마쳤습니다. 수고하셨습니다.</p>
                            <Button 
                                primary 
                                size="large" 
                                onClick={() => navigate('/quiz/dashboard')}
                                style={{ fontSize: isMobile ? '14px' : '16px', borderRadius: '25px', padding: '12px 30px', backgroundColor: '#007AFF' }}
                            >
                                대시보드로 돌아가기
                            </Button>
                        </div>
                    </Container>
                </div>
            );
        } else {
            // Test Result View
            const score = Math.round((testResults.correct / testResults.total) * 100);
            return (
                <Container style={{ padding: '40px', maxWidth: '800px', minHeight: '100vh', backgroundColor: '#FBFBFD' }}>
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: '40px', 
                        borderRadius: '24px', 
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        textAlign: 'center',
                        marginBottom: '30px'
                    }}>
                        <Header as="h1" style={{ fontSize: '64px', color: score >= 70 ? '#34C759' : '#FF3B30', margin: '0 0 10px 0' }}>
                            {score}점
                        </Header>
                        <Header as="h3" style={{ color: '#1d1d1f', marginTop: 0 }}>
                            총 {testResults.total}문제 중 {testResults.correct}문제를 맞혔습니다.
                        </Header>
                        <Button 
                            primary 
                            size="large" 
                            onClick={() => navigate('/quiz/dashboard')}
                            style={{ borderRadius: '25px', padding: '12px 30px', marginTop: '20px', backgroundColor: '#007AFF' }}
                        >
                            완료
                        </Button>
                    </div>

                    <Header as="h3" style={{ color: '#1d1d1f', marginLeft: '10px' }}>상세 결과</Header>
                    {testResults.details.map((item, idx) => (
                        <div key={idx} style={{ 
                            backgroundColor: 'white', 
                            padding: '24px', 
                            borderRadius: '16px', 
                            marginBottom: '16px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                            borderLeft: `4px solid ${item.is_correct ? '#34C759' : '#FF3B30'}`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'start', gap: '12px', marginBottom: '10px' }}>
                                <Icon 
                                    name={item.is_correct ? 'check circle' : 'times circle'} 
                                    style={{ color: item.is_correct ? '#34C759' : '#FF3B30', fontSize: '18px', marginTop: '2px' }} 
                                />
                                <div>
                                    <Header as="h4" style={{ margin: '0 0 8px 0', color: '#1d1d1f' }}>
                                        문제 {idx + 1}
                                    </Header>
                                    <p style={{ fontSize: '16px', color: '#1d1d1f', lineHeight: '1.5' }}>{item.text}</p>
                                </div>
                            </div>
                            
                            <div style={{ marginLeft: '30px', marginTop: '16px', backgroundColor: '#F5F5F7', padding: '12px', borderRadius: '8px' }}>
                                <p style={{ margin: 0, fontSize: '14px', color: '#424245' }}><strong>나의 답안:</strong> {item.user_answer || '(답안 없음)'}</p>
                            </div>

                            {!item.is_correct && (
                                <Message negative size="tiny" style={{ marginLeft: '30px', marginTop: '12px', borderRadius: '8px' }}>
                                    <Message.Header>피드백</Message.Header>
                                    <p>{item.feedback}</p>
                                </Message>
                            )}
                        </div>
                    ))}
                </Container>
            );
        }
    }

    // --- Question View ---
    const isMC = currentQuestion.question_type === 'MC';
    const hasResult = !!results[currentQuestion.id];
    
    // Parse options if MC
    let options = [];
    if (isMC && currentQuestion.answer_meta && currentQuestion.answer_meta.options) {
        options = currentQuestion.answer_meta.options;
    }

    return (
        <div style={{ height: 'calc(100vh - 50px)', backgroundColor: '#FBFBFD', display: 'flex', flexDirection: 'column', paddingTop: '80px', overflow: 'auto' }}>
            <div style={{ 
                padding: '16px 0', 
                backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                position: 'fixed',
                top: 50,
                left: 0,
                right: 0,
                zIndex: 100
            }}>
                <Container style={{ maxWidth: '800px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#86868b', fontSize: '14px' }}>
                            문제 {currentIndex + 1} / {questions.length}
                        </span>
                        {mode === 'test' && timeLeft !== null && (
                            <span style={{ 
                                fontWeight: '600', 
                                fontSize: '16px', 
                                color: timeLeft < 60 ? '#FF3B30' : '#1d1d1f',
                                fontVariantNumeric: 'tabular-nums',
                                minWidth: '60px',
                                textAlign: 'center'
                            }}>
                                {formatTime(timeLeft)}
                            </span>
                        )}
                        <Button 
                            icon="close" 
                            basic 
                            circular
                            size="mini" 
                            onClick={() => {
                                if(window.confirm("퀴즈를 종료하시겠습니까? 진행 상황이 저장되지 않습니다.")) navigate('/quiz/dashboard');
                            }}
                            style={{ boxShadow: 'none' }}
                        />
                    </div>
                    <Progress percent={progress} size="tiny" color="blue" active style={{ margin: 0, borderRadius: '4px' }} />
                </Container>
            </div>

            <Container style={{ padding: '20px', maxWidth: '800px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {/* Question Card */}
                <Card fluid style={{ 
                    padding: isMobile ? '10px' : '40px', 
                    borderRadius: '24px', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                    border: 'none',
                    marginBottom: '30px'
                }}>
                    <Card.Content>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
                            <Card.Header style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '600', lineHeight: '1.5', color: '#1d1d1f', flex: 1 }}>
                                {currentQuestion.question_text}
                            </Card.Header>
                            {isAdmin && (
                                <Button
                                    icon="edit"
                                    size="mini"
                                    basic
                                    circular
                                    title="문제 상세 / 편집"
                                    onClick={() => window.open(`/admin/quiz-questions/${currentQuestion.id}`, '_blank')}
                                    style={{ marginLeft: '12px', flexShrink: 0, boxShadow: 'none' }}
                                />
                            )}
                        </div>
                        
                        {isMC ? (
                            <Form>
                                {options.map((opt, idx) => (
                                    <div 
                                        key={idx} 
                                        onClick={() => !hasResult && handleOptionSelect(opt)}
                                        style={{ 
                                            padding: '16px 20px', 
                                            border: answers[currentQuestion.id] === opt ? '2px solid #007AFF' : '2px solid #d2d2d7', 
                                            borderRadius: '12px',
                                            backgroundColor: answers[currentQuestion.id] === opt ? '#F0F8FF' : 'white',
                                            cursor: hasResult ? 'default' : 'pointer',
                                            marginBottom: '12px',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        onMouseEnter={(e) => {
                                            if(!hasResult && answers[currentQuestion.id] !== opt) e.currentTarget.style.backgroundColor = '#F5F5F7';
                                        }}
                                        onMouseLeave={(e) => {
                                            if(!hasResult && answers[currentQuestion.id] !== opt) e.currentTarget.style.backgroundColor = 'white';
                                        }}
                                    >
                                        <Radio
                                            checked={answers[currentQuestion.id] === opt}
                                            disabled={mode === 'practice' && hasResult}
                                            style={{ marginRight: '10px' }}
                                        />
                                        <span style={{ fontSize: isMobile ? '14px' : '16px', color: '#1d1d1f' }}>{opt}</span>
                                    </div>
                                ))}
                            </Form>
                        ) : (
                            <Form>
                                <TextArea 
                                    placeholder="답변을 입력하세요..." 
                                    value={answers[currentQuestion.id] || ''}
                                    onChange={handleTextChange}
                                    rows={6}
                                    disabled={mode === 'practice' && hasResult}
                                    style={{ 
                                        borderRadius: '12px', 
                                        padding: '16px', 
                                        fontSize: '16px', 
                                        border: '1px solid #d2d2d7',
                                        backgroundColor: (mode === 'practice' && hasResult) ? '#F5F5F7' : 'white'
                                    }}
                                />
                            </Form>
                        )}

                        {/* Practice Mode Feedback */}
                        {mode === 'practice' && hasResult && (
                            <div style={{ 
                                marginTop: '30px', 
                                padding: '20px', 
                                borderRadius: '16px', 
                                backgroundColor: results[currentQuestion.id].is_correct ? '#F2FCF5' : '#FFF5F5',
                                border: `1px solid ${results[currentQuestion.id].is_correct ? '#34C759' : '#FF3B30'}`
                            }}>
                                <Header as="h4" style={{ color: results[currentQuestion.id].is_correct ? '#34C759' : '#FF3B30', margin: '0 0 10px 0' }}>
                                    <Icon name={results[currentQuestion.id].is_correct ? 'check circle' : 'times circle'} />
                                    {results[currentQuestion.id].is_correct ? '정답입니다!' : '오답입니다.'}
                                </Header>
                                <p style={{ fontSize: '15px', lineHeight: '1.5', color: '#1d1d1f' }}>
                                    {results[currentQuestion.id].feedback}
                                </p>
                                {currentQuestion.citation_url && (
                                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                        <a href={currentQuestion.citation_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0071e3', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Icon name="external" /> 
                                            출처: {currentQuestion.citation_source || '참고 자료'}
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}

                    </Card.Content>
                </Card>

                {/* Actions */}
                <div style={{ textAlign: 'center' }}>
                    {mode === 'practice' ? (
                        <>
                            {!hasResult ? (
                                <Button 
                                    primary 
                                    size="large" 
                                    onClick={handleCheckAnswer}
                                    disabled={!answers[currentQuestion.id] || isSubmitting}
                                    loading={isSubmitting}
                                    style={{ 
                                        width: '100%', 
                                        maxWidth: '320px', 
                                        borderRadius: '25px', 
                                        height: '50px', 
                                        backgroundColor: '#007AFF',
                                        fontSize: '16px'
                                    }}
                                >
                                    정답 확인
                                </Button>
                            ) : (
                                <Button 
                                    secondary 
                                    size="large" 
                                    onClick={handleNext}
                                    style={{ 
                                        width: '100%', 
                                        maxWidth: '320px', 
                                        borderRadius: '25px', 
                                        height: '50px',
                                        fontSize: '16px'
                                    }}
                                >
                                    {currentIndex < questions.length - 1 ? '다음 문제' : '결과 보기'}
                                </Button>
                            )}
                        </>
                    ) : (
                        // Test Mode
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                            <Button 
                                disabled={currentIndex === 0} 
                                onClick={() => setCurrentIndex(currentIndex - 1)}
                                basic
                                size="large"
                                style={{ borderRadius: '25px', minWidth: '120px' }}
                            >
                                이전
                            </Button>
                            {currentIndex < questions.length - 1 ? (
                                <Button 
                                    primary 
                                    onClick={() => setCurrentIndex(currentIndex + 1)}
                                    size="large"
                                    style={{ borderRadius: '25px', minWidth: '120px', backgroundColor: '#007AFF' }}
                                >
                                    다음
                                </Button>
                            ) : (
                                <Button 
                                    color="green" 
                                    onClick={handleSubmitTest}
                                    loading={isSubmitting}
                                    size="large"
                                    style={{ borderRadius: '25px', minWidth: '140px' }}
                                >
                                    제출하기
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default QuizSession;
